import React, { useEffect, useState, useRef } from 'react';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import moment from 'moment';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { tableExportOptions } from '../../component/helpers/table';
import SearchField from "../../component/dump/menuComponents/SearchField";
import momenttz from '../../component/helpers/momenttz';
import CustomButton from '../../component/CustomButton';
import { exportIcon } from "../../component/helpers/iconHelpers";
import { getAllDevices } from '../../action/DeviceActions';
import './BillingPage.sass';

const BillingPage = (props) => {
	const billingPageRef = useRef();
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'billingPage' });
	const { user, company, toggleLoader } = props;
	const userRole = user.roles[0];
	const [initData, setInitData] = useState([]);
	const [billingData, setBillingData] = useState([]);
	const [searchText, setSearchText] = useState('');

	useEffect(() => {
		if (company.name) {
			getBillingData(company.name);
		}
	}, [company.name]);

	const handleExportToCSV = () => {
		if (billingPageRef.current) {
			billingPageRef.current.handleExportCSV();
		}
	};

	const getBillingData = async (companyName) => {
		toggleLoader(true);

		const hubspotPayload = {
			filterGroups: [{
				filters: [{
					operator: 'CONTAINS_TOKEN',
					propertyName: 'customer_name',
					value: companyName,
				}]
			}],
			sorts: [{ propertyName: 'contract_num', direction: 'ASCENDING' }],
			limit: 100,
			properties: [
				'customer_name',
				"company_key__deal_",
				"contract_line",
				"contract_num",
				"contract_comment",
				'duration',
				'imei',
				'per_vehicle',
				'pipeline',
				'bill_start_date',
				'bill_end_date',
			],
		};

		const hubspotToken = process.env.REACT_APP_HUBSPOT_TOKEN;
		let allResults = [];
		let after = undefined;
		try {
			do {
				const body = after ? { ...hubspotPayload, after } : hubspotPayload;
				const res = await fetch('/hubspot-proxy/crm/objects/2026-03/0-3/search', {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${hubspotToken}`,
						'Content-Type': 'application/json'
					},
					body: JSON.stringify(body),
				});

				const payload = await res.json();
				const results = (payload.results || []).map(e => e.properties);
				allResults = allResults.concat(results);
				after = payload.paging?.next?.after;
			} while (after);

			setBillingData(allResults);
			setInitData(allResults);
			await fetchAllDevices(allResults);
		} catch (err) {
			console.error('HubSpot fetch error:', err);
			toastr.error('HubSpot API', err?.message || 'Request failed');
			setBillingData([]);
		} finally {
			toggleLoader(false);
		}
	}

	const fetchAllDevices = async (currentBillingData) => {
		if (!currentBillingData.length) return;
		try {
			const res = await getAllDevices(userRole, company.name, company.partner);
			const devices = _.get(res, 'data.response.devices', []);
			const expanded = currentBillingData.flatMap((contract) => {
				const vinNumberList = contract?.vin_number?.split(',') || [];
				if (!vinNumberList.length) return [contract];
				return vinNumberList.map((vinNumber) => {
					const device = devices.find(d => d.vin_number === vinNumber);
					return {
						...contract,
						vin_number: device?.vin_number || vinNumber,
						iccid: device?.iccid || '',
						sim_status: device?.sim_status || '',
						activation_date: device?.activation_date || '',
					};
				});
			});
			setBillingData(expanded);
			setInitData(expanded);
		} catch (err) {
			console.error('getAllDevices fetch error:', err);
		}
	}

	const formatPartnerName = (cell) => {
		return company.partner;
	}

	const formatContractName = (cell, row) => {
		return <span className='contractName'>{`${cell?.toUpperCase()} - ${row?.contract_line}`}</span>;
	}

	const formatPerVehicle = (cell) => {
		const num = Number(cell);
		return num ? '$' + num.toFixed(2) : cell;
	}

	const formatTimestamp = (cell) => {
		return cell ? momenttz(cell).format('YYYY/MM/DD') : '-';
	}

	const formatId = (cell) => {
		return cell ? cell : 'N/A';
	}

	const formatSimStatus = (cell) => {
		if (cell === 'active') {
			return <span className='sim-status active'>{t('simStatusActive')}</span>;
		} else if (cell === 'pending') {
			return <span className='sim-status pending'>{t('simStatusPending')}</span>;
		} else if (cell === 'suspended') {
			return <span className='sim-status suspended'>{t('simStatusSuspended')}</span>;
		} else if (!cell) {
			return 'N/A';
		}
	}

	const dataSearch = (e) => {
		const value = e ? e.target.value.toLowerCase() : searchText;
		const searchedData = _.cloneDeep(initData).filter(e => {
			return (
				(e.customer_name && e.customer_name.toString().toLowerCase().includes(value))
				|| (e.contract_num && `${e.contract_num?.toUpperCase()} - ${e.contract_line}`.includes(value))
				|| (e.imei && e.imei.toLowerCase().includes(value))
			)
		})

		setBillingData(searchedData);
		setSearchText(value);
	}

	return (
		<div className="billing-page-main">
			<div className="page-subheader">
				<div className="subheader-section title">
					<h2>{t('titleActiveContracts')}</h2>
					<span className="mainDescription"> {t('mainDescription')}</span>
				</div>
				<div className="subheader-section search">
					<SearchField term={searchText} onSearch={dataSearch} placeholder={t('placeholderSearchContracts')} />
				</div>
				<div className="billing-page-btn-section">
					<div className="subheader-section filter-section">
						<CustomButton
							onClick={handleExportToCSV}
							size='sm'
							variant='primary-outline'
							prefix='export-button'
						>
							{exportIcon}
							<span className='export-label'>{t('titleExport')}</span>
						</CustomButton>
					</div>
				</div>
			</div>

			<main className="main-content-block">
				<BootstrapTable
					data={billingData}
					{...tableExportOptions}
					ref={billingPageRef}
					exportCSV={false}
					csvFileName={`billing_data_${momenttz().format('YYYY-MM-DD_HH-mm-ss')}.csv`}
				>
					<TableHeaderColumn
						isKey
						width="10%"
						dataField="customer_name"
						dataSort
					>
						{t('headerCustomerName')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="customer_name"
						dataSort
						dataFormat={formatPartnerName}
						dataFormatCSV={formatPartnerName}
					>
						{t('headerPartner')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="contract_num"
						dataFormat={formatContractName}
						dataFormatCSV={formatContractName}
						dataSort
					>
						{t('headerContractNumber')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="bill_start_date"
						dataSort
						dataFormat={formatTimestamp}
						dataFormatCSV={formatTimestamp}
					>
						{t('headerBillStartDate')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="bill_end_date"
						dataSort
						dataFormat={formatTimestamp}
						dataFormatCSV={formatTimestamp}
					>
						{t('headerBillEndDate')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="duration"
						dataSort
					>
						{t('headerDuration')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="per_vehicle"
						dataFormat={formatPerVehicle}
						dataFormatCSV={formatPerVehicle}
						dataSort
					>
						{t('headerPerVehicle')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="imei"
						dataFormat={formatId}
						dataFormatCSV={formatId}
						dataSort
					>
						{t('headerImei')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="iccid"
						dataFormat={formatId}
						dataFormatCSV={formatId}
						dataSort
					>
						{t('headerIccid')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="sim_status"
						dataFormat={formatSimStatus}
						dataFormatCSV={formatSimStatus}
						dataSort
					>
						{t('headerSimStatus')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="activation_date"
						dataSort
						dataFormat={formatTimestamp}
						dataFormatCSV={formatTimestamp}
					>
						{t('headerActivationDate')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="last_connection_date"
						dataSort
						dataFormat={formatTimestamp}
						dataFormatCSV={formatTimestamp}
					>
						{t('headerLastConnectionDate')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="deactivation_date"
						dataSort
						dataFormat={formatTimestamp}
						dataFormatCSV={formatTimestamp}
					>
						{t('headerDeactivationDate')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="vin"
						dataSort
					>
						{t('headerVin')}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="10%"
						dataField="hs_object_id"
						dataSort
					>
						{t('headerOrder')}
					</TableHeaderColumn>
				</BootstrapTable>
			</main>
		</div >
	)
}

export default connect(
	state => ({
		user: state.user.user,
		company: state.company.company
	}),
	dispatch => ({
		toggleLoader: (show) => {
			dispatch({ type: 'TOGGLE_LOADER', payload: show });
		}
	})
)(BillingPage);